<template>
  <div class="info-slot-container significant-event-list-slot">
    <Transition name="slide-left-right">
      <div v-if="!isShowDetails">
        <div class="d-flex">
          <div class="d-flex">
            <h5>{{ $t('custom_forms.significant_event')}}</h5>
          </div>
          <feather-icon @click="showFiltersToggle" icon="FilterIcon" size="16" class="ml-1 cursor-pointer"/>
        </div>
        <div class="significant-event-list-filters">
          <div v-if="showFilters">
            <h6 class="ml-1">{{ $tc("actions.filter", 0) }}</h6>
            <div class="custom-dates-filter d-flex mb-1">
              <div class="from ml-1">
                {{ $t("action_plan.list.filters.start_date") }}
                <flat-pickr
                  id="fromDateSignificantEvent"
                  v-model="fromDate"
                  :config="datePickerConfig"
                  :disabled="!!currentDateWillcard"
                  class="timeNoBackground"
                />
              </div>
              <div class="ml-1">
                {{ $t("action_plan.list.filters.end_date") }}
                <flat-pickr
                  id="toDateSignificantEvent"
                  v-model="toDate"
                  :config="datePickerConfig"
                  :disabled="!!currentDateWillcard"
                  class="timeNoBackground"
                />
              </div>
              <span v-if="fromDate || toDate" class="d-flex align-items-center">
                <feather-icon @click="clearDatesRanges" icon="XCircleIcon" size="16" class="ml-1 cursor-pointer"/>
              </span>
            </div>

            <div class="d-flex gap-1">
              <div
                v-for="(dateType, index) in dateWillcardsOptions"
                :key="dateType"
                class="ml-1"
              >
                <b-form-checkbox
                  :name="`option-filter-date-${index}`"
                  :checked="currentDateWillcard === dateType"
                  :disabled="
                    (currentDateWillcard && currentDateWillcard !== dateType) ||
                    !!fromDate ||
                    !!toDate"
                  @change="handleDateWillcardChange(dateType, $event)"
                >
                  {{ $t(`operational_security.${dateType}`) }}
                </b-form-checkbox>
              </div>
            </div>
          </div>
          <div class="mb-1 mt-2">
            <label class="text-capitalize">{{ $t("table.show") }} </label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
              />
            <label class="text-capitalize">{{ $t("table.entries") }} </label>

            <b-button
              type="button"
              variant="success"
              class="text-capitalize ml-1"
              size="sm"
              v-b-tooltip.hover.button.viewport="`${$t('detection_event.export_excel')}`"
              @click="exportSignificantEvents('excel')"
            >
              <feather-icon icon="DownloadCloudIcon" size="15" />
            </b-button>

            <b-button
              type="button"
              variant="danger"
              class="text-capitalize ml-1"
              size="sm"
              v-b-tooltip.hover.button.viewport="`${$t('grid.slots.info.pdf_export')}`"
              @click="exportSignificantEvents('pdf')"
            >
              <feather-icon icon="DownloadCloudIcon" size="15" />
            </b-button>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th scope="col">
                  {{ $t('safr.date') }}
                </th>
                <th scope="col">
                  {{ $tc('operational_security.sirh_facility_name', 1) }}
                </th>
                <th scope="col">
                  {{ $t('hanwha_dvr.groups_permissions.menu.device.event') }}
                </th>
                <th scope="col">
                  {{$t('action_plan.documentTable.description')}}
                </th>
                <th scope="col">
                  {{ $t('operational_security.operational_processes.subregion') }}
                </th>
                <th scope="col">
                  {{ $t('custom_forms.security_manager') }}
                </th>
                <th scope="col">
                  {{ $t('operational_security.security_monitoring') }}
                </th>
              </tr>
            </thead>
            <tbody v-if="!isDataLoading">
              <tr
                v-for="(event, index) in significantEvents"
                :key="event.id"
                @click="showDetails(event)"
              >
                <td>{{ event.event_date }}</td>
                <td style="min-width: 200px">{{ event.facility_name || '-' }}</td>
                <td style="min-width: 200px">{{ event.subject || '-' }}</td>
                <td>
                  <div
                    class="truncate-cell"
                    :id="`description-${event.id}-${index}`"
                    :ref="`description-${event.id}-${index}`"
                  >
                    {{ event.description || '-' }}
                  </div>

                  <b-popover
                    v-if="overflowingCells[`description-${event.id}-${index}`]"
                    :target="`description-${event.id}-${index}`"
                    triggers="hover"
                    placement="bottom"
                  >
                    <div style="max-width: 400px; white-space: pre-wrap;">
                      {{ event.description }}
                    </div>
                  </b-popover>
                </td>
                <td>{{ event.subregion || '-' }}</td>
                <td style="min-width: 200px">{{ event.security_director || '-' }}</td>
                <td>
                  <div
                    class="truncate-cell"
                    :id="`director-comments-${event.id}-${index}`"
                  >
                    {{ event.director_comments || '-' }}
                  </div>

                  <b-popover
                    v-if="overflowingCells[`director-comments-${event.id}-${index}`]"
                    :target="`director-comments-${event.id}-${index}`"
                    triggers="hover"
                    placement="bottom"
                  >
                    <div style="max-width: 400px; white-space: pre-wrap;">
                      {{ event.director_comments }}
                    </div>
                  </b-popover>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td scope="col" v-for="(header, index) in 7" :key="header+index">
                  <SkeletonLoading :fullWidth="true"/>
                </td>
              </tr>
            </tbody>
          </table>
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage">
            </Pagination>
          </div>
      </div>
    </Transition>

    <Transition name="slide-left-right">
      <div v-if="isShowDetails">
        <div
          class="d-flex justify-content align-items-center mb-1 cursor-pointer"
          @click="goBack()"
          >
          <button
            class="btn btn-sm rounded-circle p-25"
            :class="isDark ? 'btn-danger' : 'btn-primary'"
          >
            <i class="feather icon-arrow-left arrow-icon"></i>
          </button>
          <div class="ml-1 text-capitalize text-dark goback-text">
            {{ $t("actions.go_back") }}
          </div>
        </div>
        <div class="">
          <b-card v-if="significantEvent">
            <div class="d-flex justify-content-end">
              <b-button
                class="mb-2"
                v-if="canStoreSignificantEvent || isSuperAdmin"
                variant="info"
                @click="updateSignificantEvent"
                :disabled="isBeingSavedSignificantEvent || (!significantEvent.subject || !significantEvent.description)"
                >
                {{ $t("actions.edit") }} {{ $t('custom_forms.significant_event') }}
              </b-button>
            </div>
            <div class="row">
              <div class="col-md-6 mb-2">
                <strong>{{ $t('safr.date') }}</strong>
                <div>{{ significantEvent.event_date }}</div>
              </div>

              <div class="col-md-6 mb-2">
                <strong>{{ $tc('operational_security.sirh_facility_name', 1) }}</strong>
                <div>{{ significantEvent.facility_name }}</div>
              </div>

              <div class="col-md-6 mb-2">
                <strong>{{ $t('hanwha_dvr.groups_permissions.menu.device.event') }}</strong>
                <b-form-input
                  v-if="(canStoreSignificantEvent && isUserSupervisor) || isSuperAdmin"
                  v-model="significantEvent.subject"
                  style="min-width: 200px"
                />
                <div v-else>{{ significantEvent.subject }}</div>
              </div>

              <div class="col-md-6 mb-2">
                <strong>{{ $t('operational_security.operational_processes.subregion') }}</strong>
                <div>{{ significantEvent.subregion || '-' }}</div>
              </div>

              <div class="col-md-6 mb-2">
                <strong>{{ $t('custom_forms.security_manager') }}</strong>
                <div>{{ significantEvent.security_director }}</div>
              </div>

              <div class="col-md-6 mb-2">
                <strong>{{ $t('operational_security.prefill.reference') }}</strong>
                <div class="d-flex reference-link" @click="goToLinkedForm">
                  <div>{{ significantEvent.form_answer.reference }}</div>
                  <feather-icon
                    icon="ExternalLinkIcon"
                    size="16"
                    class="cursor-pointer text-primary ml-1"
                  />
                </div>
              </div>

              <div class="col-md-6 mb-2">
                <strong>{{ $tc('operational_security.template.title', 1) }}</strong>
                <div>{{ significantEvent.form_answer.template }}</div>
              </div>

              <div class="col-md-6 mb-2">
                <strong>{{ $t('municipality.created_at') }}</strong>
                <div>{{ significantEvent.created_at }}</div>
              </div>

              <div class="col-12">
                <strong>{{ $t('action_plan.documentTable.description') }}</strong>

                <b-form-textarea
                  rows="2"
                  max-rows="5"
                  v-model="significantEvent.description"
                  v-if="(canStoreSignificantEvent && isUserSupervisor) || isSuperAdmin"
                  class="mb-1"
                />

                <b-card
                  v-else
                  bg-variant="gray"
                >
                  {{ significantEvent.description }}
                </b-card>
              </div>

              <div class="col-12">
                <strong>{{ $t('operational_security.sec_manager_comments') }}</strong>

                <b-form-textarea
                  rows="2"
                  max-rows="5"
                  v-model="significantEvent.director_comments"
                  v-if="canStoreSignificantEvent && !isUserSupervisor && $can('add_comment_to_significant_event', 'OpSecurityFormAnswers')"
                />

                <b-card
                  v-else
                  bg-variant="gray"
                >
                  {{ significantEvent.director_comments || '-' }}
                </b-card>
              </div>
            </div>
          </b-card>
        </div>
      </div>

    </Transition>
  </div>
</template>

<script>
import { watch } from '@vue/composition-api';
import { useSignificantEvents } from '@/composables/useSignificantEvents';
import vSelect from "vue-select";
import Pagination from "@/components/Pagination.vue";
import flatPickr from "vue-flatpickr-component";
import SkeletonLoading from "@/components/SkeletonLoading.vue";

export default {
  components: {
    vSelect,
    Pagination,
    flatPickr,
    SkeletonLoading
  },
  props: {
  },
  setup(_, context) {

    const {
      significantEvents,
      overflowingCells,
      perPage,
      perPageOptions,
      totalList,
      currentPage,
      showFilters,
      dateWillcardsOptions,
      currentDateWillcard,
      toDate,
      fromDate,
      datePickerConfig,
      isDataLoading,
      isShowDetails,
      isDark,
      significantEvent,
      canStoreSignificantEvent,
      isUserSupervisor,
      isSuperAdmin,
      isRegionalDirector,
      isBeingSavedSignificantEvent,
      checkOverflow,
      getsignificantEvents,
      update,
      showFiltersToggle,
      handleDateWillcardChange,
      clearDatesRanges,
      exportSignificantEvents,
      goBack,
      showDetails,
      goToLinkedForm,
      updateSignificantEvent
    } = useSignificantEvents();

    getsignificantEvents()

    watch(significantEvents, checkOverflow);

    watch([ currentPage, perPage, fromDate, toDate, currentDateWillcard],
    () => {
      getsignificantEvents()
    })

    return {
      significantEvents,
      overflowingCells,
      perPage,
      perPageOptions,
      totalList,
      currentPage,
      showFilters,
      dateWillcardsOptions,
      currentDateWillcard,
      toDate,
      fromDate,
      datePickerConfig,
      isDataLoading,
      isShowDetails,
      isDark,
      significantEvent,
      canStoreSignificantEvent,
      isUserSupervisor,
      isSuperAdmin,
      isRegionalDirector,
      isBeingSavedSignificantEvent,
      checkOverflow,
      update,
      showFiltersToggle,
      handleDateWillcardChange,
      clearDatesRanges,
      exportSignificantEvents,
      goBack,
      showDetails,
      goToLinkedForm,
      updateSignificantEvent
    };
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/variables/_variables.scss";
.significant-event-list-slot {
  padding: 1rem;
  background-color: #fff;
  border-radius: 0.5rem;
  overflow: scroll;
}
tbody tr:hover {
  background-color: #f1eeee;
}
.dark-layout {
    .form-group label {
      color: $theme-dark-label-color;
    }

    .form-control {
      background-color: $theme-dark-input-bg;
      border-color: $theme-dark-input-border-color;
      color: $theme-dark-body-color;

      &::placeholder {
        color: $theme-dark-input-placeholder-color;
      }

      &:focus {
        background-color: $theme-dark-card-bg;
        border-color: var(--primary);
        color: $theme-dark-headings-color;
      }
    }

    .table {
      background-color: $theme-dark-table-bg;
      color: $theme-dark-body-color;

      thead th {
        background-color: $theme-dark-table-header-bg;
        border-color: $theme-dark-border-color;
        color: $theme-dark-headings-color;
      }

      tbody td {
        border-color: $theme-dark-border-color;
      }

      tbody tr:hover {
        background-color: $theme-dark-table-hover-bg;
      }
    }
  }
.truncate-cell {
  display: -webkit-box;
  -webkit-line-clamp: 6; /* adjust lines */
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
}
.slide-right-left-enter-active,
.slide-right-left-leave-active {
  transition: transform 0.2s ease;
}

.slide-right-left-enter-from {
  transform: translateX(100%);
}

.slide-right-left-enter-to {
  transform: translateX(0%);
}

.slide-right-left-leave-from {
  transform: translateX(0%);
}

.slide-right-left-leave-to {
  transform: translateX(100%);
}

.slide-left-right-enter-active,
.slide-left-right-leave-active {
  transition: transform 0.2s ease;
}

.slide-left-right-enter-from {
  transform: translateX(-100%);
}

.slide-left-right-enter-to {
  transform: translateX(0%);
}

.slide-left-right-leave-from {
  transform: translateX(0%);
}

.slide-left-right-leave-to {
  transform: translateX(-100%);
}
.table-responsive {
  background: #fff;
}
.reference-link {
  cursor: pointer;
  color: #0052cc;
  text-decoration: none;
}
</style>
